import { TAddToFavoriteParameters, addToFavorite as _addToFavorite } from '../services/addToFavorite';
import { setCheckedAuthentication, setIsAuthenticated } from '../slices/authenticationSlice';
import { getIsAuthenticated } from '../services/getIsAuthenticated';
import { createAppAsyncThunk } from '../utils/redux/createAppAsyncThunk';
import { TOfferAnalyticsData, TRecommendation } from '../types/recommendation';
import { trackAddToFavorite } from './tracking';
import { addSavedOffer } from '@cian/favorites-assign-widget';
import { EEntityType } from '../repositories/feed-page/entities/recommendations/RecItemV2';
import {
  finishFavoriteUpdate,
  rollbackFavoriteUpdate,
  startFavoriteUpdate,
} from '../slices/recommendationsSlice/recommendationsSlice';
import { TDeviceType } from '@cian/ui-kit';
import { toUnknownAction } from '../utils/redux/toUnknownAction';

type TAddToFavoriteThunkParameters = {
  deviceType: TDeviceType;
  recommendation: TRecommendation;
  rollbackOnError?: boolean;
};

export const ADD_TO_FAVORITE_REQUEST_ERROR = 'add-to-favorite-request-error' as const;

export function isAddToFavoriteRequestError(error: unknown): error is { code: typeof ADD_TO_FAVORITE_REQUEST_ERROR } {
  return typeof error === 'object' && error !== null && 'code' in error && error.code === ADD_TO_FAVORITE_REQUEST_ERROR;
}

class AddToFavoriteRequestError extends Error {
  public readonly code = ADD_TO_FAVORITE_REQUEST_ERROR;

  public constructor(error: unknown) {
    super(error instanceof Error ? error.message : 'Failed to add an offer to favorites');
    this.name = 'AddToFavoriteRequestError';
  }
}

export const addToFavorite = createAppAsyncThunk(
  'recommendations/addToFavorite',
  async (
    { recommendation, deviceType, rollbackOnError = false }: TAddToFavoriteThunkParameters,
    { getState, extra: context, dispatch, requestId },
  ) => {
    dispatch(startFavoriteUpdate({ offerId: recommendation.offerId as number, isFavorite: true, requestId }));

    const {
      landingType,
      authentication: { isCheckedAuthentication, isAuthenticated },
    } = getState();

    const { offerId, dealType, entityType, analyticsData } = recommendation;

    // Проверка авторизации для подборок. Делается один раз за сеанс для всех рекомендаций
    /* istanbul ignore next */
    const isAuthenticatedResultPromise = isCheckedAuthentication
      ? Promise.resolve(isAuthenticated)
      : getIsAuthenticated(context);
    void isAuthenticatedResultPromise.catch(() => undefined);

    let addToFavoriteResult: Awaited<ReturnType<typeof _addToFavorite>>;

    try {
      addToFavoriteResult = await _addToFavorite(context, {
        offerId,
        dealType,
        entityType,
      } as TAddToFavoriteParameters);
    } catch (error) {
      if (rollbackOnError) {
        dispatch(rollbackFavoriteUpdate({ offerId: recommendation.offerId as number, isFavorite: false, requestId }));
      } else {
        dispatch(finishFavoriteUpdate({ offerId: recommendation.offerId as number, requestId }));
      }

      throw new AddToFavoriteRequestError(error);
    }

    try {
      const isAuthenticatedResult = await isAuthenticatedResultPromise;

      dispatch(setCheckedAuthentication());

      if (isAuthenticatedResult && !isAuthenticated) {
        dispatch(setIsAuthenticated());
      }

      const label = `${recommendation.dealType}/${
        recommendation.entityType === EEntityType.OfferSuburban ? 'suburban' : 'flat'
      }`;

      trackAddToFavorite({ deviceType, label, landingType, products: (analyticsData as TOfferAnalyticsData).products });

      const isCurrentFavoriteUpdate =
        getState().recommendations.favoriteUpdateRequestIds?.[offerId as number] === requestId;

      if (!isCurrentFavoriteUpdate) {
        return;
      }

      const userHasFolders = Boolean(addToFavoriteResult.hasFolders);
      const folder = addToFavoriteResult.folder;
      /* istanbul ignore next */
      const savedInFolders =
        folder && folder.name && folder.id
          ? [
              {
                id: folder.id,
                name: folder.name,
              },
            ]
          : null;

      dispatch(toUnknownAction(addSavedOffer({ offerIds: [offerId as number], savedInFolders, userHasFolders })));
    } finally {
      dispatch(finishFavoriteUpdate({ offerId: recommendation.offerId as number, requestId }));
    }
  },
);
